import { Select } from "@base-ui/react/select";
import { LIMITS, type ListOccasion } from "@listou/shared/constants";
import { catalogueStickerUrl } from "@listou/shared/stickers";
import type { CatalogueResponse } from "@listou/shared/types/api";
import type { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { Sticker } from "./list-page/parts";
import { ProductCard } from "./product-card";
import { Spinner } from "./ui/spinner";

export type CatalogueSort = "popular" | "rating" | "trend";

export const CATALOGUE_SORTS: { value: CatalogueSort; label: string }[] = [
  { value: "popular", label: "Les plus ajoutés" },
  { value: "rating", label: "Les mieux notés" },
  { value: "trend", label: "Tendance" },
];

const OCCASIONS: { value: ListOccasion | undefined; label: string }[] = [
  { value: undefined, label: "Toutes" },
  { value: "birth", label: "Naissance" },
  { value: "christmas", label: "Noël" },
  { value: "birthday", label: "Anniversaire" },
  { value: "wedding", label: "Mariage" },
];

export type CatalogueSearch = { q?: string; occasion?: ListOccasion; sort?: CatalogueSort };

/** Everything lives in the URL, so a filtered page can be shared or bookmarked. */
export const validateCatalogueSearch = (search: Record<string, unknown>): CatalogueSearch => ({
  q:
    typeof search.q === "string" && search.q.trim()
      ? search.q.slice(0, LIMITS.searchQueryMax)
      : undefined,
  occasion: OCCASIONS.some((option) => option.value && option.value === search.occasion)
    ? (search.occasion as ListOccasion)
    : undefined,
  sort:
    CATALOGUE_SORTS.some((option) => option.value === search.sort) && search.sort !== "popular"
      ? (search.sort as CatalogueSort)
      : undefined,
});

/** Occasion chips as links on the current page, so the filter keeps the rest of the URL. */
export function OccasionFilter({ current }: { current: ListOccasion | undefined }) {
  return (
    <nav aria-label="Occasion" className="flex flex-wrap gap-2">
      {OCCASIONS.map((option) => (
        <Link
          key={option.label}
          to="."
          search={(prev: CatalogueSearch) => ({ ...prev, occasion: option.value })}
          aria-current={current === option.value ? "page" : undefined}
          className={`rounded-full border-2 px-4 py-1.5 font-bold transition ${
            current === option.value
              ? "border-ink bg-ink text-surface"
              : "border-line hover:border-ink/40"
          }`}
        >
          {option.label}
        </Link>
      ))}
    </nav>
  );
}

export function SortSelect({
  value,
  onChange,
}: {
  value: CatalogueSort;
  onChange: (value: CatalogueSort) => void;
}) {
  return (
    <Select.Root value={value} onValueChange={(next) => next && onChange(next as CatalogueSort)}>
      <div className="flex items-center gap-2">
        <Select.Label className="font-bold text-muted">Trier</Select.Label>
        <Select.Trigger className="flex h-11 min-w-48 items-center justify-between gap-3 rounded-full border-2 border-line bg-surface px-4 font-bold hover:border-ink/40 data-[popup-open]:border-accent">
          <Select.Value>
            {CATALOGUE_SORTS.find((option) => option.value === value)?.label}
          </Select.Value>
          <Select.Icon className="text-muted">▾</Select.Icon>
        </Select.Trigger>
      </div>
      <Select.Portal>
        <Select.Positioner sideOffset={6} className="z-50">
          <Select.Popup className="min-w-[var(--anchor-width)] rounded-2xl border-2 border-line bg-surface p-1.5 text-ink shadow-xl">
            <Select.List>
              {CATALOGUE_SORTS.map((option) => (
                <Select.Item
                  key={option.value}
                  value={option.value}
                  className="flex min-h-11 cursor-pointer items-center justify-between gap-3 rounded-xl px-3 font-bold outline-none select-none data-[highlighted]:bg-ink/[0.07]"
                >
                  <Select.ItemText>{option.label}</Select.ItemText>
                  <Select.ItemIndicator>✓</Select.ItemIndicator>
                </Select.Item>
              ))}
            </Select.List>
          </Select.Popup>
        </Select.Positioner>
      </Select.Portal>
    </Select.Root>
  );
}

/** The grid with its loading, error, empty and "more" states. */
export function CatalogueResults({
  catalogue,
  emptyTitle,
  emptyAction,
}: {
  catalogue: UseInfiniteQueryResult<InfiniteData<CatalogueResponse>>;
  emptyTitle: string;
  emptyAction?: ReactNode;
}) {
  const products = catalogue.data?.pages.flatMap((page) => page.products) ?? [];
  return (
    <>
      {catalogue.isPending && <Spinner className="size-8 justify-self-center" />}
      {catalogue.isError && (
        <p className="text-danger">
          Les idées n'ont pas pu être chargées. Réessayez dans un instant.
        </p>
      )}

      {catalogue.isSuccess && products.length === 0 && (
        <div className="relative grid justify-items-center gap-4 rounded-[2rem] bg-[#eef4fa] px-6 py-16 text-center">
          <div className="flex gap-2" aria-hidden>
            <Sticker src={catalogueStickerUrl("loupe")} className="w-20 -rotate-6" />
            <Sticker src={catalogueStickerUrl("cadeau")} className="w-20 rotate-6" />
          </div>
          <h2 className="text-3xl font-semibold">{emptyTitle}</h2>
          <p className="max-w-[46ch] text-lg text-ink/80">
            Les idées apparaissent dès que des parents les ajoutent à leurs listes ou les notent.
            Essayez un autre mot, ou une autre occasion.
          </p>
          {emptyAction}
        </div>
      )}

      {products.length > 0 && (
        <ul className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
          {products.map((product) => (
            <li key={product.id}>
              <ProductCard product={product} />
            </li>
          ))}
        </ul>
      )}

      {catalogue.hasNextPage && (
        <button
          type="button"
          className="btn-secondary justify-self-center"
          onClick={() => void catalogue.fetchNextPage()}
          disabled={catalogue.isFetchingNextPage}
        >
          {catalogue.isFetchingNextPage ? "Chargement…" : "Voir plus d'idées"}
        </button>
      )}
    </>
  );
}
